import {
    Autocomplete,
    createFilterOptions,
    FilterOptionsState,
    TextFieldProps
} from '@mui/material';

import { useTheme } from '@dh/hooks';

import { DigitalHubChip } from '../buttons';
import { StyledTextField } from '.';

import type { JSX } from 'react';

type DHAutocompleteProps<Value> = {
    ariaLabel: string;
    options: Value[];
    name: string;
    value: Value[];
    label: string;
    onChange: (key: string, value: Value[]) => void;
    getOptionId: (option: Value) => string;
    getOptionLabel: (option: Value) => string;
    isRequired?: boolean;
    disabled?: boolean;
    specialOptionRenderCondition?: (option: Value) => boolean;
    SpecialOptionComp?: ({ children }: { children: React.ReactNode }) => JSX.Element;
    specialOptionFilterer?: (option: Value, state: FilterOptionsState<Value>) => boolean;
    errorText?: string;
} & Pick<TextFieldProps, 'error' | 'helperText'>;

export const DHAutocomplete = <Value,>({
    ariaLabel,
    options,
    name,
    value,
    label,
    onChange,
    getOptionId,
    getOptionLabel,
    isRequired = false,
    disabled = false,
    error,
    errorText,
    helperText,
    specialOptionRenderCondition,
    specialOptionFilterer,
    SpecialOptionComp
}: DHAutocompleteProps<Value>) => {
    const { colors, spacing } = useTheme();

    return (
        <Autocomplete
            disabled={disabled}
            multiple
            aria-label={ariaLabel}
            options={options}
            filterSelectedOptions
            filterOptions={
                // prevent special option from ever being filtered out, if it exists
                SpecialOptionComp
                    ? (options, state) =>
                          options.filter(
                              (option: Value) =>
                                  specialOptionRenderCondition?.(option) ||
                                  specialOptionFilterer?.(option, state)
                          )
                    : createFilterOptions()
            }
            isOptionEqualToValue={(option, value) => getOptionId(option) === getOptionId(value)}
            value={value}
            onChange={(_, value) => {
                onChange(name, value);
            }}
            renderValue={(tagValue, getTagProps) => {
                return (
                    tagValue
                        // special option never rendered as chip
                        .filter((tag) => !specialOptionRenderCondition?.(tag))
                        .map((tag, index) => (
                            <DigitalHubChip
                                {...getTagProps({ index })}
                                key={index}
                                backgroundColor={colors.primaryContainer}
                                label={getOptionLabel(tag)}
                            />
                        ))
                );
            }}
            getOptionLabel={getOptionLabel}
            renderOption={(props, option) => {
                const isSpecialOption = specialOptionRenderCondition?.(option);
                const index = (props as any)['data-option-index'];
                return (
                    <li
                        {...props}
                        key={getOptionId(option)}
                        style={
                            isSpecialOption && index !== 0
                                ? {
                                      borderTop: `1px solid ${colors.surfaceVariant}`,
                                      paddingTop: spacing(1.5)
                                  }
                                : undefined
                        }
                    >
                        {SpecialOptionComp && isSpecialOption ? (
                            <SpecialOptionComp>{getOptionLabel(option)}</SpecialOptionComp>
                        ) : (
                            getOptionLabel(option)
                        )}
                    </li>
                );
            }}
            renderInput={(params) => (
                <StyledTextField
                    required={isRequired}
                    {...params}
                    key={params.id}
                    label={label}
                    error={error}
                    helperText={error ? errorText : helperText}
                    slotProps={{
                        ...params.InputProps,
                        input: {
                            ...params.InputProps,
                            style: {
                                minHeight: spacing(7),
                                height: 'auto',
                                alignItems: 'flex-start',
                                paddingTop: spacing(1)
                            }
                        }
                    }}
                />
            )}
        />
    );
};
